/**
 * @file API 基礎方法
 * @description 提供 HTTP 請求的基礎方法 (GET, POST, PUT, PATCH, DELETE)，
 * 包含自動 loading 狀態管理和錯誤處理
 */
import fetchJson, { fetchBlob, FetchOptions } from './fetch';
import Swal from 'sweetalert2';
import store from 'store/store';
import { open, close } from 'store/slice/loadingSlice';
import ExceptionHandleService from 'utils/exceptionHandler';

const _EHS = new ExceptionHandleService({
  _NAME: 'service/base.ts',
  _NOTICE: 'api method function'
});

/** 追蹤當前活躍的請求數量 */
let activeRequests = 0;

/**
 * 開啟 Loading 狀態
 * @description 當第一個請求開始時顯示 loading
 */
const openLoading = (): void => {
  if (activeRequests === 0) {
    try {
      store.dispatch(open());
    } catch (error) {
      _EHS.errorReport(error, 'openLoading', _EHS._LEVEL.ERROR);
    }
  }
  activeRequests++;
};

/**
 * 關閉 Loading 狀態
 * @description 當所有請求完成時隱藏 loading
 */
const closeLoading = (): void => {
  activeRequests--;
  if (activeRequests === 0) {
    try {
      store.dispatch(close());
    } catch (error) {
      _EHS.errorReport(error, 'closeLoading', _EHS._LEVEL.ERROR);
    }
  }
};

/**
 * 發送 GET 請求
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const get = async <T = unknown>(
  url: string,
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'GET';
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'get', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 POST 請求
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - 請求 body
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const post = async <T = unknown, B extends object = Record<string, unknown>>(
  url: string,
  body: B = {} as B,
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'POST';
    options.body = JSON.stringify(body);
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'post', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 PUT 請求
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - 請求 body
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const put = async <T = unknown, B extends object = Record<string, unknown>>(
  url: string,
  body: B = {} as B,
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'PUT';
    options.body = JSON.stringify(body);
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'put', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 POST 請求 (FormData 格式)
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - FormData 或物件
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const postFormData = async <T = unknown>(
  url: string,
  body: FormData | Record<string, unknown> = {},
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'POST';
    options.body = body as BodyInit;
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'postFormData', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 PATCH 請求 (FormData 格式)
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - FormData 或物件
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const patchFormData = async <T = unknown>(
  url: string,
  body: FormData | Record<string, unknown> = {},
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'PATCH';
    options.body = body as BodyInit;
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'patchFormData', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 PATCH 請求
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - 請求 body
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const patch = async <T = unknown, B extends object = Record<string, unknown>>(
  url: string,
  body: B = {} as B,
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'PATCH';
    options.body = JSON.stringify(body);
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'patch', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 DELETE 請求
 * @template T - 回應資料型別
 * @param url - 請求 URL
 * @param body - 請求 body
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<T> - 回應資料
 */
export const del = async <T = unknown>(
  url: string,
  body: Record<string, unknown> = {},
  options: FetchOptions = {},
  isLoading = true
): Promise<T> => {
  if (isLoading) openLoading();
  try {
    options.method = 'DELETE';
    options.body = JSON.stringify(body);
    options.credentials = 'include';
    const res = await fetchJson(url, options);
    return res as T;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'del', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 POST 請求並取得 Blob 回應
 * @param url - 請求 URL
 * @param body - 請求 body
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<Blob> - Blob 資料
 */
export const postBlob = async (
  url: string,
  body: Record<string, unknown> = {},
  options: FetchOptions = {},
  isLoading = true
): Promise<Blob | unknown> => {
  if (isLoading) openLoading();
  try {
    options.method = 'POST';
    options.body = JSON.stringify(body);
    options.credentials = 'include';
    const res = await fetchBlob(url, options);
    return res;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'postBlob', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

/**
 * 發送 GET 請求並取得 Blob 回應
 * @param url - 請求 URL
 * @param options - 請求選項
 * @param isLoading - 是否顯示 loading (預設 true)
 * @returns Promise<Blob> - Blob 資料
 */
export const getBlob = async (
  url: string,
  options: FetchOptions = {},
  isLoading = true
): Promise<Blob | unknown> => {
  if (isLoading) openLoading();
  try {
    options.method = 'GET';
    options.credentials = 'include';
    const res = await fetchBlob(url, options);
    return res;
  } catch (error) {
    const err = error as Error;
    Swal.fire('ERROR', `fetch: ${url} | ${err.message}`, 'error');
    _EHS.errorReport(error, 'getBlob', _EHS._LEVEL.ERROR);
    return Promise.reject(err.message);
  } finally {
    if (isLoading) closeLoading();
  }
};

export default {
  get,
  post,
  put,
  postFormData,
  patch,
  del,
  postBlob,
  getBlob,
  patchFormData
};
